import { yupResolver } from '@hookform/resolvers/yup'
import { FormHelperText, TextField } from '@mui/material'
import { Button } from 'components/Button/Button'
import { useUser } from 'hooks/user'
import { useCallback, useMemo, useState } from 'react'
import React from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { PATH, PROGRESS_TYPES } from 'utils/constants'
import getTestId from 'utils/getTestId'
import { confirmPasswordSchema, newPasswordSchema } from 'utils/validationSchema'
import logoAvtodor from 'images/branding/avtodor/logo.png'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'

import { AccService } from 'api/schema/services/AccService'
import clsx from 'clsx'
import { COMPANY, CompanyName } from 'config'

import styleAvtodor from '../Layout/avtodor/style.module.css'
import styleSkyeer from '../Layout/skyeer/style.module.css'

import { branding } from 'utils/branding'

interface IFormInputs {
  password: string
  password2: string
}

const NewPassword: React.FC = () => {
  const [progress, setProgress] = useState(PROGRESS_TYPES.IDLE)
  const navigate = useNavigate()

  const user = useUser()
  const { t } = useTranslation()
  const [urlParams] = useSearchParams()

  const token = urlParams.get('token')

  const loginProgress = useMemo(() => user?.state.loginProgress, [user?.state.loginProgress])
  const methods = useForm<IFormInputs>({
    resolver: yupResolver(yup.object().shape({ password: newPasswordSchema(), password2: confirmPasswordSchema() })),
  })

  const onSubmit: SubmitHandler<IFormInputs> = useCallback(
    async data => {
      if (data.password !== data.password2) {
        return
      }
      try {
        setProgress(PROGRESS_TYPES.WORK)
        await AccService.accPasswordResetConfirmCreate({ token: token || '', password: data.password })
        setProgress(PROGRESS_TYPES.SUCCESS)
      } catch (error) {
        setProgress(PROGRESS_TYPES.ERROR)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const SigninLayout = branding.getSigninLayout()

  const style = COMPANY === CompanyName.AVTODOR ? styleAvtodor : styleSkyeer

  return (
    <SigninLayout>
      <form className={style.signinBlock} onSubmit={methods.handleSubmit(onSubmit)} autoComplete="off">
        {COMPANY === CompanyName.AVTODOR ? <img src={logoAvtodor} alt="logo" className={style.logo} /> : null}
        {progress !== PROGRESS_TYPES.SUCCESS && (
          <>
            <div className={style.enterText}>{t('new_password.enter_new_password')}</div>
            <div className={style.fld}>
              <TextField
                {...methods.register('password')}
                id="password"
                name="password"
                type="password"
                variant="standard"
                inputProps={{
                  'data-test-id': 'password',
                }}
                aria-describedby="password-error-text"
                error={!!methods.formState.errors.password}
                fullWidth
              />
              <FormHelperText id="password-error-text">{methods.formState.errors.password?.message}</FormHelperText>
            </div>
            <div className={style.fld}>
              <div className={style.enterText}>{t('new_password.repeat_password')}</div>
              <TextField
                {...methods.register('password2')}
                id="password2"
                name="password2"
                type="password"
                variant="standard"
                inputProps={{
                  'data-test-id': 'password2',
                }}
                aria-describedby="password-error-text2"
                error={!!methods.formState.errors.password2}
                fullWidth
              />
              <FormHelperText id="password-error-text2">
                {methods.formState.errors.password2?.message}
                {progress === PROGRESS_TYPES.ERROR && t('new_password.error_changing')}
              </FormHelperText>
            </div>

            <Button.Standart
              dataTestId={'submit-btn'}
              disabled={loginProgress === PROGRESS_TYPES.WORK}
              loading={loginProgress === PROGRESS_TYPES.WORK}
              className={clsx(style.signinButton, style.newPasswordButton)}
            >
              {t('new_password.set_new_password')}
            </Button.Standart>
            <div data-test-id={'restore'} className={style.restoreText}>
              {t('restore_password.have_credentials')}
              <Link className={style.href} to={PATH.SIGNIN} data-test-id={getTestId('link-forgot-password')}>
                {t('restore_password.sign_in')}
              </Link>
            </div>
          </>
        )}
        {progress === PROGRESS_TYPES.SUCCESS && (
          <>
            <div className={style.restoreText}>{t('new_password.password_set')}</div>
            <Button.Standart
              dataTestId={'signin-btn'}
              disabled={false}
              className={style.signinButton}
              onClick={() => navigate(PATH.MAIN)}
            >
              {t('new_password.login_button')}
            </Button.Standart>
          </>
        )}
      </form>
    </SigninLayout>
  )
}

export default NewPassword
